import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Link,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Cancel as CancelIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { googleMapsQueryUrl, formatJobDeliveryLine } from '../../utils/jobMapsLink';
import { formatFuelQuantities, formatDeliveredQuantities } from '../../utils/formatFuelQuantities';
import CompletedJobDetailsDialog from '../../components/CompletedJobDetailsDialog';
import JobListCard from '../../components/mobile/JobListCard';
import PageHeader from '../../components/mobile/PageHeader';
import { useIsMobile, useIsCompact } from '../../hooks/useIsMobile';

const CustomerOrders = () => {
  const isMobile = useIsMobile();
  const isCompact = useIsCompact();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    delivery_address: '',
    delivery_city: '',
    delivery_state: 'WA',
    delivery_zip: '',
    delivery_notes: '',
    diesel_quantity: '',
    def_quantity: '',
    frequency: 'one-time',
    scheduled_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState('');
  const [viewingJob, setViewingJob] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/customers/profile/jobs/');
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setEditingOrder(null);
    setFormData({
      delivery_address: '',
      delivery_city: '',
      delivery_state: 'WA',
      delivery_zip: '',
      delivery_notes: '',
      diesel_quantity: '',
      def_quantity: '',
      frequency: 'one-time',
      scheduled_date: '',
    });
    setMessage('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setOpenEditDialog(false);
    setEditingOrder(null);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    // Format scheduled_date for datetime-local input
    let formattedDate = '';
    if (order.scheduled_date) {
      const date = new Date(order.scheduled_date);
      // Convert to local datetime string format (YYYY-MM-DDTHH:mm)
      formattedDate = date.toISOString().slice(0, 16);
    }
    
    setFormData({
      delivery_address: order.delivery_address || '',
      delivery_city: order.delivery_city || '',
      delivery_state: order.delivery_state || '',
      delivery_zip: order.delivery_zip || '',
      delivery_notes: order.delivery_notes || '',
      diesel_quantity: order.diesel_quantity || '',
      def_quantity: order.def_quantity || '',
      frequency: order.frequency || 'one-time',
      scheduled_date: formattedDate,
    });
    setOpenEditDialog(true);
    setMessage('');
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    setCancelling(true);
    setMessage('');

    try {
      await api.post(`/jobs/${orderId}/cancel/`);
      setMessage('Order cancelled successfully!');
      fetchJobs();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error cancelling order.';
      setMessage(errorMsg);
      alert(errorMsg);
    } finally {
      setCancelling(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const orderData = {
        delivery_address: formData.delivery_address,
        delivery_city: formData.delivery_city,
        delivery_state: formData.delivery_state,
        delivery_zip: formData.delivery_zip,
        delivery_notes: formData.delivery_notes || '',
        diesel_quantity: parseFloat(formData.diesel_quantity) || 0,
        def_quantity: parseFloat(formData.def_quantity) || 0,
        frequency: formData.frequency,
      };

      if (formData.scheduled_date) {
        orderData.scheduled_date = formData.scheduled_date;
      }

      if (editingOrder) {
        // Update existing order
        await api.patch(`/jobs/${editingOrder.id}/`, orderData);
        setMessage('Order updated successfully!');
      } else {
        // Create new order
        await api.post('/jobs/', orderData);
        setMessage('Order created successfully!');
      }
      
      fetchJobs();
      setTimeout(() => {
        handleCloseDialog();
        setMessage('');
      }, 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      `Error ${editingOrder ? 'updating' : 'creating'} order. Please check your input.`;
      setMessage(errorMsg);
      console.error(`Error ${editingOrder ? 'updating' : 'creating'} order:`, error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      accepted: 'info',
      active: 'primary',
      complete: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const handleViewCompletedJob = async (job) => {
    try {
      const response = await api.get(`/jobs/${job.id}/`);
      setViewingJob(response.data);
    } catch (error) {
      console.error('Error fetching job details:', error);
      setViewingJob(job);
    }
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingJob(null);
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const activeJobs = jobs.filter((j) => ['pending', 'accepted', 'active'].includes(j.job_status));
  const completedJobs = jobs.filter((j) => j.job_status === 'complete');
  const displayedJobs = tabValue === 0 ? activeJobs : completedJobs;

  return (
    <Layout>
      <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
        <PageHeader
          title="My Orders"
          action={(
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={isMobile ? { minHeight: 44, width: '100%' } : undefined}
            >
              Create Order
            </Button>
          )}
        />

        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={isMobile ? `Active (${activeJobs.length})` : `Active Orders (${activeJobs.length})`} />
          <Tab label={isMobile ? `Done (${completedJobs.length})` : `Completed Orders (${completedJobs.length})`} />
        </Tabs>

        {isMobile ? (
          <Box>
            {displayedJobs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                No {tabValue === 0 ? 'active' : 'completed'} orders found
              </Typography>
            ) : (
              displayedJobs.map((job) => {
                const fullAddress = formatJobDeliveryLine(job) || 'N/A';
                const canEdit = job.job_status === 'pending';
                const canCancel = ['pending', 'accepted', 'active'].includes(job.job_status);
                const delivered = formatDeliveredQuantities(job);

                return (
                  <JobListCard
                    key={job.id}
                    title={`Order #${job.id.substring(0, 8)}`}
                    subtitle={fullAddress !== 'N/A' ? (
                      <Link
                        href={googleMapsQueryUrl(fullAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {fullAddress}
                      </Link>
                    ) : fullAddress}
                    chips={[
                      <Chip key="status" label={job.job_status} color={getStatusColor(job.job_status)} size="small" />,
                      <Chip
                        key="payment"
                        label={job.payment_status}
                        color={job.payment_status === 'paid' ? 'success' : 'warning'}
                        size="small"
                      />,
                    ]}
                    actions={(
                      <>
                        {canEdit && (
                          <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditOrder(job)}
                            sx={{ minHeight: 44 }}
                          >
                            Edit
                          </Button>
                        )}
                        {job.job_status === 'complete' && (
                          <Button
                            variant="outlined"
                            color="info"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewCompletedJob(job)}
                            sx={{ minHeight: 44 }}
                          >
                            Details
                          </Button>
                        )}
                        {canCancel && (
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => handleCancelOrder(job.id)}
                            disabled={cancelling}
                            sx={{ minHeight: 44 }}
                          >
                            Cancel
                          </Button>
                        )}
                      </>
                    )}
                    fields={[
                      { label: 'Fuel', value: formatFuelQuantities(job), fullWidth: true },
                      ...(delivered ? [{ label: 'Delivered', value: delivered, fullWidth: true }] : []),
                      { label: 'Driver', value: job.driver_name || 'Not assigned' },
                      { label: 'Created', value: new Date(job.created_at).toLocaleDateString() },
                    ]}
                  />
                );
              })
            )}
          </Box>
        ) : (
        <TableContainer 
          component={Paper}
          sx={{
            overflowX: 'auto',
            maxWidth: '100%',
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Fuel (US gal)</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No {tabValue === 0 ? 'active' : 'completed'} orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedJobs.map((job) => {
                  const fullAddress = formatJobDeliveryLine(job) || 'N/A';
                  const canEdit = job.job_status === 'pending';
                        const canCancel = ['pending', 'accepted', 'active'].includes(job.job_status);
                  
                  return (
                    <TableRow key={job.id}>
                      <TableCell>{job.id.substring(0, 8)}</TableCell>
                      <TableCell>
                        {fullAddress !== 'N/A' ? (
                          <Link
                            href={googleMapsQueryUrl(fullAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                          >
                            {fullAddress}
                          </Link>
                        ) : (
                          fullAddress
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box>
                            {job.diesel_quantity > 0 && `${job.diesel_quantity} gal Diesel`}
                            {job.diesel_quantity > 0 && job.def_quantity > 0 && ' / '}
                            {job.def_quantity > 0 && `${job.def_quantity} gal DEF`}
                            {job.diesel_quantity === 0 && job.def_quantity === 0 && 'N/A'}
                          </Box>
                          {(job.delivered_diesel > 0 || job.delivered_def > 0) && (
                            <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              Delivered: {' '}
                              {job.delivered_diesel > 0 && `${job.delivered_diesel} gal Diesel`}
                              {job.delivered_diesel > 0 && job.delivered_def > 0 && ' / '}
                              {job.delivered_def > 0 && `${job.delivered_def} gal DEF`}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{job.frequency}</TableCell>
                      <TableCell>
                        <Chip
                          label={job.job_status}
                          color={getStatusColor(job.job_status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.payment_status}
                          color={job.payment_status === 'paid' ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{job.driver_name || 'Not assigned'}</TableCell>
                      <TableCell>{job.vehicle_info || 'Not assigned'}</TableCell>
                      <TableCell>{job.invoice_number || 'N/A'}</TableCell>
                      <TableCell>{new Date(job.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {job.job_status === 'complete' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="info"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleViewCompletedJob(job)}
                            >
                              View Details
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditOrder(job)}
                            >
                              Edit
                            </Button>
                          )}
                          {canCancel && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelOrder(job.id)}
                              disabled={cancelling}
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Create/Edit Order Dialog */}
        <Dialog 
          open={openDialog || openEditDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
          fullScreen={isCompact}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>{editingOrder ? 'Edit Order' : 'Create New Order'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {message && (
                <Alert 
                  severity={message.includes('success') ? 'success' : 'error'} 
                  sx={{ mb: 2 }}
                >
                  {message}
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1E3A5F' }}>
                    Delivery Location
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    name="delivery_address"
                    value={formData.delivery_address}
                    onChange={handleChange}
                    required
                    placeholder="Enter street address"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="delivery_city"
                    value={formData.delivery_city}
                    onChange={handleChange}
                    required
                    placeholder="Enter city"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth required>
                    <InputLabel>State</InputLabel>
                    <Select
                      name="delivery_state"
                      value={formData.delivery_state}
                      onChange={handleChange}
                      label="State"
                    >
                      <MenuItem value="AL">AL - Alabama</MenuItem>
                      <MenuItem value="AK">AK - Alaska</MenuItem>
                      <MenuItem value="AZ">AZ - Arizona</MenuItem>
                      <MenuItem value="AR">AR - Arkansas</MenuItem>
                      <MenuItem value="CA">CA - California</MenuItem>
                      <MenuItem value="CO">CO - Colorado</MenuItem>
                      <MenuItem value="CT">CT - Connecticut</MenuItem>
                      <MenuItem value="DE">DE - Delaware</MenuItem>
                      <MenuItem value="FL">FL - Florida</MenuItem>
                      <MenuItem value="GA">GA - Georgia</MenuItem>
                      <MenuItem value="HI">HI - Hawaii</MenuItem>
                      <MenuItem value="ID">ID - Idaho</MenuItem>
                      <MenuItem value="IL">IL - Illinois</MenuItem>
                      <MenuItem value="IN">IN - Indiana</MenuItem>
                      <MenuItem value="IA">IA - Iowa</MenuItem>
                      <MenuItem value="KS">KS - Kansas</MenuItem>
                      <MenuItem value="KY">KY - Kentucky</MenuItem>
                      <MenuItem value="LA">LA - Louisiana</MenuItem>
                      <MenuItem value="ME">ME - Maine</MenuItem>
                      <MenuItem value="MD">MD - Maryland</MenuItem>
                      <MenuItem value="MA">MA - Massachusetts</MenuItem>
                      <MenuItem value="MI">MI - Michigan</MenuItem>
                      <MenuItem value="MN">MN - Minnesota</MenuItem>
                      <MenuItem value="MS">MS - Mississippi</MenuItem>
                      <MenuItem value="MO">MO - Missouri</MenuItem>
                      <MenuItem value="MT">MT - Montana</MenuItem>
                      <MenuItem value="NE">NE - Nebraska</MenuItem>
                      <MenuItem value="NV">NV - Nevada</MenuItem>
                      <MenuItem value="NH">NH - New Hampshire</MenuItem>
                      <MenuItem value="NJ">NJ - New Jersey</MenuItem>
                      <MenuItem value="NM">NM - New Mexico</MenuItem>
                      <MenuItem value="NY">NY - New York</MenuItem>
                      <MenuItem value="NC">NC - North Carolina</MenuItem>
                      <MenuItem value="ND">ND - North Dakota</MenuItem>
                      <MenuItem value="OH">OH - Ohio</MenuItem>
                      <MenuItem value="OK">OK - Oklahoma</MenuItem>
                      <MenuItem value="OR">OR - Oregon</MenuItem>
                      <MenuItem value="PA">PA - Pennsylvania</MenuItem>
                      <MenuItem value="RI">RI - Rhode Island</MenuItem>
                      <MenuItem value="SC">SC - South Carolina</MenuItem>
                      <MenuItem value="SD">SD - South Dakota</MenuItem>
                      <MenuItem value="TN">TN - Tennessee</MenuItem>
                      <MenuItem value="TX">TX - Texas</MenuItem>
                      <MenuItem value="UT">UT - Utah</MenuItem>
                      <MenuItem value="VT">VT - Vermont</MenuItem>
                      <MenuItem value="VA">VA - Virginia</MenuItem>
                      <MenuItem value="WA">WA - Washington</MenuItem>
                      <MenuItem value="WV">WV - West Virginia</MenuItem>
                      <MenuItem value="WI">WI - Wisconsin</MenuItem>
                      <MenuItem value="WY">WY - Wyoming</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    name="delivery_zip"
                    value={formData.delivery_zip}
                    onChange={handleChange}
                    required
                    placeholder="12345"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Notes"
                    name="delivery_notes"
                    value={formData.delivery_notes}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Enter delivery notes and special instructions"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Diesel quantity (gallons)"
                    name="diesel_quantity"
                    type="number"
                    value={formData.diesel_quantity}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Enter diesel quantity in gallons"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DEF quantity (gallons)"
                    name="def_quantity"
                    type="number"
                    value={formData.def_quantity}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Enter DEF quantity in gallons"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Frequency"
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="one-time">One-time</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Scheduled Date (Optional)"
                    name="scheduled_date"
                    type="datetime-local"
                    value={formData.scheduled_date}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="Leave empty for immediate delivery"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving 
                  ? (editingOrder ? 'Updating...' : 'Creating...') 
                  : (editingOrder ? 'Update Order' : 'Create Order')
                }
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <CompletedJobDetailsDialog
          open={viewDialogOpen}
          job={viewingJob}
          onClose={handleCloseViewDialog}
          isMobile={isCompact}
        />
      </Container>
    </Layout>
  );
};

export default CustomerOrders;



